<template>
  <div class="manual-research-bar">
    <div class="research-container">
      <!-- Research Icon -->
      <div class="research-icon">
        <i class="fas fa-search"></i>
      </div>

      <!-- Search Input -->
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Manual Research Search - Enter medical terms, topics, or concepts..."
        class="research-input"
        @keyup.enter="performSearch"
        @focus="showSuggestions = true"
      />

      <!-- Quick Suggestions (if available from video context) -->
      <div v-if="showSuggestions && suggestedTerms.length > 0" class="suggestions-dropdown">
        <div class="suggestions-header">
          <i class="fas fa-lightbulb"></i>
          Suggested from this video
        </div>
        <button
          v-for="term in suggestedTerms.slice(0, 5)"
          :key="term"
          class="suggestion-item"
          @click="selectTerm(term)"
        >
          {{ term }}
        </button>
      </div>

      <!-- Database Selection -->
      <div class="database-checkboxes">
        <label
          v-for="connection in availableConnections"
          :key="connection.id"
          class="db-checkbox"
          :title="connection.name"
        >
          <input
            v-model="selectedConnections"
            type="checkbox"
            :value="connection.id"
          />
          <i class="fas fa-database"></i>
          <span class="db-name">{{ connection.name }}</span>
        </label>
      </div>

      <!-- Search Button -->
      <button
        @click="performSearch"
        :disabled="!searchQuery.trim() || selectedConnections.length === 0"
        class="search-btn"
        title="Search scientific literature"
      >
        <i class="fas fa-search"></i>
        Search
      </button>

      <!-- Recent Searches -->
      <div v-if="recentSearches.length > 0" class="recent-searches-toggle">
        <button @click="showRecent = !showRecent" class="recent-btn" title="Recent searches">
          <i class="fas fa-history"></i>
        </button>
        
        <div v-if="showRecent" class="recent-dropdown">
          <div class="recent-header">Recent Searches</div>
          <button
            v-for="search in recentSearches"
            :key="search"
            class="recent-item"
            @click="selectRecent(search)"
          >
            <i class="fas fa-clock"></i>
            {{ search }}
          </button>
          <button @click="clearRecent" class="clear-recent">
            <i class="fas fa-trash-alt"></i>
            Clear History
          </button>
        </div>
      </div>
    </div>

    <!-- Search Results Modal -->
    <teleport to="body">
      <div v-if="showResults" class="research-results-modal" @click.self="closeResults">
        <div class="results-container">
          <div class="results-header">
            <h2>
              <i class="fas fa-search"></i>
              Research Results for "{{ lastSearch }}"
            </h2>
            <button @click="closeResults" class="close-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="results-content">
            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p>Searching scientific literature...</p>
            </div>

            <!-- Results List -->
            <div v-else-if="results.length > 0" class="results-list">
              <div
                v-for="result in results"
                :key="result.id"
                class="result-item"
              >
                <h3>
                  <a :href="result.url" target="_blank" rel="noopener noreferrer">
                    {{ result.title }}
                  </a>
                </h3>
                <div class="result-meta">
                  <span v-if="result.authors" class="authors">{{ result.authors }}</span>
                  <span v-if="result.journal" class="journal">{{ result.journal }}</span>
                  <span v-if="result.year" class="year">{{ result.year }}</span>
                </div>
                <p class="result-abstract">{{ result.abstract }}</p>
                <div class="result-actions">
                  <a :href="result.url" target="_blank" class="btn btn-sm btn-primary">
                    <i class="fas fa-external-link-alt"></i>
                    View Article
                  </a>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-results">
              <i class="fas fa-search"></i>
              <p>No results found for "{{ lastSearch }}"</p>
              <small>Try different search terms or check spelling</small>
            </div>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'

export default {
  name: 'ManualResearchBar',
  props: {
    videoContext: {
      type: Object,
      default: () => ({})
    },
    medicalTerms: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    const searchQuery = ref('')
    const showSuggestions = ref(false)
    const showRecent = ref(false)
    const showResults = ref(false)
    const loading = ref(false)
    const results = ref([])
    const lastSearch = ref('')
    const recentSearches = ref([])
    const availableConnections = ref([])
    const selectedConnections = ref([])

    // Load API connections and recent searches
    onMounted(async () => {
      const stored = localStorage.getItem('recentResearch')
      if (stored) {
        recentSearches.value = JSON.parse(stored)
      }
      
      // Load available API connections
      try {
        const api = (await import('@/services/api.js')).default
        const response = await api.get('/api-connections/')
        const connections = response.data.results || response.data
        
        // Filter to only show NCBI/scientific database connections
        availableConnections.value = connections.filter(c => 
          c.api_template && (
            c.api_template.api_type === 'NCBI_EUTILS' ||
            c.name.toLowerCase().includes('pubmed') ||
            c.name.toLowerCase().includes('ncbi')
          )
        )
        
        // Auto-select all connections by default
        selectedConnections.value = availableConnections.value.map(c => c.id)
      } catch (err) {
        console.error('Error loading API connections:', err)
      }
    })

    // Get suggested terms from video context
    const suggestedTerms = computed(() => {
      if (props.medicalTerms && props.medicalTerms.length > 0) {
        return props.medicalTerms.map(t => t.term || t)
      }
      return []
    })

    const performSearch = async () => {
      if (!searchQuery.value.trim()) return

      lastSearch.value = searchQuery.value
      loading.value = true
      showResults.value = true
      showSuggestions.value = false

      // Add to recent searches
      addToRecent(searchQuery.value)

      try {
        // TODO: Replace with actual API call to PubMed or research service
        // For now, simulating search
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        results.value = [
          {
            id: 1,
            title: `Research result for "${searchQuery.value}"`,
            authors: 'Smith J, Johnson A, Williams B',
            journal: 'Journal of Veterinary Research',
            year: '2024',
            abstract: 'This is a sample abstract that would contain the research findings...',
            url: `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(searchQuery.value)}`
          }
        ]
      } catch (error) {
        console.error('Research search error:', error)
        results.value = []
      } finally {
        loading.value = false
      }
    }

    const searchPubMed = () => {
      if (!searchQuery.value.trim()) return
      
      addToRecent(searchQuery.value)
      const pubmedUrl = `https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(searchQuery.value)}`
      window.open(pubmedUrl, '_blank')
    }

    const selectTerm = (term) => {
      searchQuery.value = term
      showSuggestions.value = false
      performSearch()
    }

    const selectRecent = (search) => {
      searchQuery.value = search
      showRecent.value = false
      performSearch()
    }

    const addToRecent = (search) => {
      // Remove if already exists
      recentSearches.value = recentSearches.value.filter(s => s !== search)
      // Add to beginning
      recentSearches.value.unshift(search)
      // Keep only last 10
      recentSearches.value = recentSearches.value.slice(0, 10)
      // Save to localStorage
      localStorage.setItem('recentResearch', JSON.stringify(recentSearches.value))
    }

    const clearRecent = () => {
      recentSearches.value = []
      localStorage.removeItem('recentResearch')
      showRecent.value = false
    }

    const closeResults = () => {
      showResults.value = false
    }

    // Close suggestions when clicking outside
    watch(searchQuery, () => {
      if (!searchQuery.value.trim()) {
        showSuggestions.value = false
      }
    })

    return {
      searchQuery,
      showSuggestions,
      showRecent,
      showResults,
      loading,
      results,
      lastSearch,
      recentSearches,
      suggestedTerms,
      availableConnections,
      selectedConnections,
      performSearch,
      searchPubMed,
      selectTerm,
      selectRecent,
      clearRecent,
      closeResults
    }
  }
}
</script>

<style scoped>
.manual-research-bar {
  background: var(--color-surface, #fff);
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
}

.research-container {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
}

.research-icon {
  font-size: 20px;
  color: var(--color-primary, #00a79d);
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.research-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid var(--color-border, #e0e0e0);
  border-radius: 8px;
  font-size: 15px;
  transition: all 0.2s;
}

.research-input:focus {
  outline: none;
  border-color: var(--color-primary, #00a79d);
  box-shadow: 0 0 0 3px rgba(0,167,157,0.1);
}

.database-checkboxes {
  display: flex;
  gap: 8px;
  align-items: center;
}

.db-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  color: var(--color-text, #111);
  white-space: nowrap;
}

.db-checkbox:hover {
  background: var(--color-background, #f9f9f9);
  border-color: var(--color-primary, #00a79d);
}

.db-checkbox input[type="checkbox"] {
  cursor: pointer;
  margin: 0;
}

.db-checkbox i {
  color: var(--color-primary, #00a79d);
  font-size: 12px;
}

.db-name {
  font-weight: 500;
}

.search-btn,
.pubmed-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.search-btn {
  background: var(--color-primary, #00a79d);
  color: #fff;
}

.search-btn:hover:not(:disabled) {
  background: var(--color-secondary, #5ac8c0);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,167,157,0.3);
}

.pubmed-btn {
  background: #fff;
  color: var(--color-primary, #00a79d);
  border: 2px solid var(--color-primary, #00a79d);
}

.pubmed-btn:hover:not(:disabled) {
  background: var(--color-primary, #00a79d);
  color: #fff;
}

.search-btn:disabled,
.pubmed-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.suggestions-dropdown,
.recent-dropdown {
  position: absolute;
  bottom: 100%;
  left: 52px;
  right: 200px;
  background: #fff;
  border: 1px solid var(--color-border, #e0e0e0);
  border-radius: 8px;
  box-shadow: 0 -4px 12px rgba(0,0,0,0.1);
  margin-bottom: 8px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
}

.suggestions-header,
.recent-header {
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-light, #666);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid var(--color-border, #e0e0e0);
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggestion-item,
.recent-item {
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  transition: background 0.2s;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.suggestion-item:hover,
.recent-item:hover {
  background: var(--color-background, #f9f9f9);
}

.recent-searches-toggle {
  position: relative;
}

.recent-btn {
  width: 40px;
  height: 40px;
  border: 1px solid var(--color-border, #e0e0e0);
  background: #fff;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: var(--color-text-light, #666);
  transition: all 0.2s;
}

.recent-btn:hover {
  background: var(--color-background, #f9f9f9);
  color: var(--color-primary, #00a79d);
}

.recent-dropdown {
  right: 0;
  left: auto;
  min-width: 300px;
}

.clear-recent {
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-top: 1px solid var(--color-border, #e0e0e0);
  background: none;
  text-align: left;
  cursor: pointer;
  color: #dc2626;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.clear-recent:hover {
  background: #fef2f2;
}

/* Results Modal */
.research-results-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.results-container {
  background: #fff;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px rgba(0,0,0,0.2);
}

.results-header {
  padding: 24px;
  border-bottom: 1px solid var(--color-border, #e0e0e0);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.results-header h2 {
  font-size: 20px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: var(--color-text-light, #666);
  transition: all 0.2s;
}

.close-btn:hover {
  background: var(--color-background, #f9f9f9);
  color: var(--color-text, #111);
}

.results-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border, #e0e0e0);
  border-top-color: var(--color-primary, #00a79d);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  padding-bottom: 24px;
  border-bottom: 1px solid var(--color-border, #e0e0e0);
}

.result-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.result-item h3 {
  font-size: 18px;
  margin: 0 0 8px 0;
}

.result-item h3 a {
  color: var(--color-primary, #00a79d);
  text-decoration: none;
}

.result-item h3 a:hover {
  text-decoration: underline;
}

.result-meta {
  display: flex;
  gap: 12px;
  font-size: 13px;
  color: var(--color-text-light, #666);
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.result-abstract {
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text, #111);
  margin: 0 0 12px 0;
}

.empty-results {
  text-align: center;
  padding: 60px 20px;
  color: var(--color-text-light, #666);
}

.empty-results i {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}
</style>

